import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PAYROLL_DURATIONS,
  PAYROLL_MODES,
  type PayrollCycle,
  type PayrollDuration,
  type PayrollMode,
} from '@tailoring-crm/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';
import { useUpsertPayrollCycle } from '../../lib/payroll/use-payroll.js';

/**
 * The three facts a shop's payroll hangs off: how it pays, over what window, counted from when.
 *
 * Both option lists are built from the shared enums rather than typed out here, so a mode or a
 * duration added to the contract cannot quietly go missing from the one screen that sets it —
 * and every option is labelled from the locale file, so no raw `piecewise` ever reaches a shop.
 */
export interface PayrollCycleDialogProps {
  /** The configuration in hand, or nothing when this shop has never set one. */
  cycle: PayrollCycle | null;
  /** What the duration control starts on when there is nothing configured yet. */
  fallbackDuration: PayrollDuration;
  onClose: () => void;
}

export function PayrollCycleDialog({
  cycle,
  fallbackDuration,
  onClose,
}: PayrollCycleDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const save = useUpsertPayrollCycle();

  const [mode, setMode] = useState<PayrollMode>(cycle?.mode ?? 'piecewise');
  const [duration, setDuration] = useState<PayrollDuration>(cycle?.duration ?? fallbackDuration);
  const [cycleStartDate, setCycleStartDate] = useState(() => asDateInput(cycle));
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      await save.mutateAsync({ mode, duration, cycleStartDate, active: true });
      toast({ title: t('payroll.cycleSaved'), variant: 'success' });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('payroll.cycleDialogTitle')}</DialogTitle>
          <DialogDescription>{t('payroll.cycleDialogHint')}</DialogDescription>
        </DialogHeader>

        {error ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>
        ) : null}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payroll-cycle-mode">{t('payroll.wagesBasis')}</Label>
            <select
              id="payroll-cycle-mode"
              value={mode}
              onChange={(event) => setMode(event.target.value as PayrollMode)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              {PAYROLL_MODES.map((one) => (
                <option key={one} value={one}>
                  {t(`payroll.mode.${one}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payroll-cycle-duration">{t('payroll.salaryDuration')}</Label>
            <select
              id="payroll-cycle-duration"
              value={duration}
              onChange={(event) => setDuration(event.target.value as PayrollDuration)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              {PAYROLL_DURATIONS.map((one) => (
                <option key={one} value={one}>
                  {t(`payroll.duration.${one}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payroll-cycle-start">{t('payroll.cycleStartDate')}</Label>
            <Input
              id="payroll-cycle-start"
              type="date"
              value={cycleStartDate}
              onChange={(event) => setCycleStartDate(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={cycleStartDate === '' || save.isPending} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** What `<input type="date">` wants: the configured anchor, or today for a shop with none. */
function asDateInput(cycle: PayrollCycle | null): string {
  return (cycle?.cycleStartDate ?? new Date().toISOString()).slice(0, 10);
}
